// Turn-structure metadata. Pure. No React, no Three.js, no
// GameBoard reference. Owns the canonical answer to "which phases
// does a Sorcery turn have, and what discrete steps fire inside each
// phase, in what order?" GameBoard's _runPhaseSteps consumes this
// metadata and dispatches each step to an imperative routine; adding
// or reordering rules means editing this file plus the dispatcher
// switch. There is no third place to chase down.
//
// ── Codex references ──
//
//   Turn Overview. "Your turn has three phases: Start phase, Main
//                    phase, End phase."
//   Start Phase. In order:
//                   1. End all "Until next turn" effects.
//                   2. Untap all your cards.
//                   3. All your sites (and other mana sources) provide
//                      their mana for this turn.
//                   4. Trigger and resolve abilities that happen at
//                      the start of the turn.
//                   5. Draw a card from spellbook or atlas. (First
//                      player skips on their first turn.)
//   Main Phase. Interactive. Players cast spells and activate
//                   abilities at will. No automatic steps.
//   End Phase. In order:
//                   1. Trigger end-of-turn abilities.
//                   2. Players remove all damage from their minions
//                      in the realm (Avatars do NOT auto-heal).
//                   3. Effects that last "this turn" end.
//                   4. Any remaining mana is lost.
//                   5. "After each turn" abilities resolve.
//
// ── Shape ──
//
// PHASE_STEPS[phase] is an ordered array of step descriptors:
//   { id: string, description: string, trigger?: string }
//
// `id`. Stable identifier the dispatcher switch keys on.
//                Adding a new rule = pick a new id, add to the metadata,
//                add a case to GameBoard._runPhaseStep.
// `description`. Codex-rooted explanation for log + future tooltip use.
// `trigger`. When the step's job is to fire a named automation
//                trigger, list it here. The dispatcher reuses this for
//                its applyCardAutomation call so future trigger names
//                propagate without code changes.

export const PHASE = Object.freeze({
  START: 'start',
  MAIN:  'main',
  END:   'end',
});

export const PHASE_STEPS = Object.freeze({
  [PHASE.START]: Object.freeze([
    // Vaults of Zul: "skip their next turn." Consumed first so a
    // skipped turn runs none of the other start steps (no untap, no
    // mana, no draw). The turn simply begins and immediately passes.
    Object.freeze({
      id: 'consume_skip_next_turn',
      description: 'If this turn is being skipped, pass immediately',
    }),
    Object.freeze({
      id: 'end_until_next_turn',
      description: 'End all "Until next turn" effects',
    }),
    Object.freeze({
      id: 'untap_all',
      description: 'Untap all of your cards',
    }),
    Object.freeze({
      id: 'provide_mana',
      description: 'Mana sources provide mana for this turn',
    }),
    // draw_card is pushed onto the LIFO storyline BEFORE
    // fire_on_turn_start so the latter ends up on top and resolves
    // first. Codex: "Triggered abilities at the start of your turn
    // happen now." Seer (and any other on_turn_start trigger) must
    // resolve before the player picks Spell vs Site.
    Object.freeze({
      id: 'draw_card',
      description: 'Draw a card from spellbook or atlas (skip first player first turn)',
    }),
    Object.freeze({
      id: 'fire_on_turn_start',
      description: 'Resolve start-of-turn triggers',
      trigger: 'on_turn_start',
    }),
  ]),
  [PHASE.MAIN]: Object.freeze([
    // Main phase is interactive. No automatic steps fire here.
  ]),
  [PHASE.END]: Object.freeze([
    Object.freeze({
      id: 'fire_on_turn_end',
      description: 'Resolve end-of-turn triggers',
      trigger: 'on_turn_end',
    }),
    Object.freeze({
      id: 'expire_turn_lifespans',
      description: 'Decrement "lasts N of your turns" counters; dispel any that reach 0',
    }),
    Object.freeze({
      id: 'heal_minions',
      description: 'Remove all damage from minions in the realm (Avatars excluded)',
    }),
    Object.freeze({
      id: 'end_this_turn_effects',
      description: 'End effects that last "this turn"',
    }),
    Object.freeze({
      id: 'lose_unused_mana',
      description: 'Any remaining mana is lost',
    }),
    Object.freeze({
      id: 'fire_after_each_turn',
      description: 'Resolve "after each turn" triggers',
      trigger: 'after_each_turn',
    }),
  ]),
});

/**
 * Advance the phase pointer one tick. End → Start signals a player
 * switch; the caller is responsible for flipping the active player
 * before running the next Start phase.
 */
export function nextPhase(phase) {
  if (phase === PHASE.START) return PHASE.MAIN;
  if (phase === PHASE.MAIN)  return PHASE.END;
  return PHASE.START;
}

/** Convenience: every step id known to the engine, in firing order. */
export function allStepIds() {
  return [
    ...PHASE_STEPS[PHASE.START].map((s) => s.id),
    ...PHASE_STEPS[PHASE.MAIN].map((s) => s.id),
    ...PHASE_STEPS[PHASE.END].map((s) => s.id),
  ];
}
